import { createSlice, type PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@ui/app/store';

/**
 * Content data for a single conversation
 */
export interface ConversationContentData {
    /**
     * The selected SQL query string.
     * Can be from user click on a message or automatically set to the latest query.
     */
    sqlQuery: string | null;

    /**
     * The thought process associated with the selected message.
     * Contains structured information about the query logic.
     */
    thoughtProcess: {
        conditions?: string;
        demographics?: string;
        symptoms?: string;
    } | null;

    /**
     * Indicates if content was manually selected by user (true) or auto-selected (false).
     * Used to determine whether to update selection when new messages arrive.
     */
    isManuallySelected: boolean;

    /**
     * Timestamp of the message from which this content was selected.
     * Used for tracking and debugging purposes.
     */
    selectedTimestamp: string | null;

    /**
     * The ID of the selected message (_id field).
     * Used by the Explain feature to generate explanations for the selected response.
     */
    selectedMessageId: string | null;
}

/**
 * Selected Content State (Conversation-Scoped)
 *
 * Manages the currently selected SQL query and thought process from chat messages,
 * scoped per conversation. This ensures each conversation maintains its own independent
 * SQL selection state, allowing users to switch between conversations without losing context.
 */
export interface SelectedContentState {
    /**
     * Selected content data indexed by conversation ID.
     * Each conversation maintains its own SQL query, thought process, and selection state.
     */
    byConversation: {
        [conversationId: string]: ConversationContentData;
    };
}

const initialState: SelectedContentState = {
    byConversation: {},
};

/**
 * Get initial content data for a conversation
 */
const getInitialContentData = (): ConversationContentData => ({
    sqlQuery: null,
    thoughtProcess: null,
    isManuallySelected: false,
    selectedTimestamp: null,
    selectedMessageId: null,
});

/**
 * Redux slice for managing selected SQL query and thought process (per conversation)
 *
 * @example
 * // Select content when user clicks a message
 * dispatch(setSelectedContent({
 *   conversationId: 'conv-123',
 *   sqlQuery: "SELECT * FROM patients",
 *   thoughtProcess: { conditions: "diabetes", demographics: "age > 18" },
 *   isManuallySelected: true,
 *   selectedTimestamp: "2024-01-01T00:00:00Z"
 * }));
 *
 * @example
 * // Auto-update to latest query
 * dispatch(setSelectedContent({
 *   conversationId: 'conv-123',
 *   sqlQuery: latestQuery,
 *   thoughtProcess: latestThoughtProcess,
 *   isManuallySelected: false,
 *   selectedTimestamp: latestTimestamp
 * }));
 *
 * @example
 * // Clear selection for specific conversation
 * dispatch(clearSelectedContent('conv-123'));
 */
const selectedContentSlice = createSlice({
    name: 'chat/selectedContent',
    initialState,
    reducers: {
        /**
         * Set the selected SQL query and thought process for a conversation
         *
         * @param state - Current state
         * @param payload - Conversation ID and new selected content data
         */
        setSelectedContent(
            state,
            { payload }: PayloadAction<{ conversationId: string } & Partial<ConversationContentData>>
        ) {
            const { conversationId, ...contentData } = payload;
            if (!conversationId) return;

            // Initialize conversation data if it doesn't exist
            if (!state.byConversation[conversationId]) {
                state.byConversation[conversationId] = getInitialContentData();
            }

            // Update only provided fields
            const conversation = state.byConversation[conversationId];
            if (contentData.sqlQuery !== undefined) conversation.sqlQuery = contentData.sqlQuery;
            if (contentData.thoughtProcess !== undefined) conversation.thoughtProcess = contentData.thoughtProcess;
            if (contentData.isManuallySelected !== undefined)
                conversation.isManuallySelected = contentData.isManuallySelected;
            if (contentData.selectedTimestamp !== undefined)
                conversation.selectedTimestamp = contentData.selectedTimestamp;
            if (contentData.selectedMessageId !== undefined)
                conversation.selectedMessageId = contentData.selectedMessageId;
        },

        /**
         * Set only the SQL query for a conversation (convenience action)
         *
         * @param state - Current state
         * @param payload - Conversation ID and SQL query string
         */
        setSqlQuery(state, { payload }: PayloadAction<{ conversationId: string; sqlQuery: string | null }>) {
            const { conversationId, sqlQuery } = payload;
            if (!conversationId) return;

            if (!state.byConversation[conversationId]) {
                state.byConversation[conversationId] = getInitialContentData();
            }
            state.byConversation[conversationId].sqlQuery = sqlQuery;
        },

        /**
         * Set only the thought process for a conversation (convenience action)
         *
         * @param state - Current state
         * @param payload - Conversation ID and thought process object
         */
        setThoughtProcess(
            state,
            {
                payload,
            }: PayloadAction<{ conversationId: string; thoughtProcess: ConversationContentData['thoughtProcess'] }>
        ) {
            const { conversationId, thoughtProcess } = payload;
            if (!conversationId) return;

            if (!state.byConversation[conversationId]) {
                state.byConversation[conversationId] = getInitialContentData();
            }
            state.byConversation[conversationId].thoughtProcess = thoughtProcess;
        },

        /**
         * Mark content as manually selected by user for a conversation
         * Prevents auto-updates from overriding user selection
         *
         * @param state - Current state
         * @param payload - Conversation ID
         */
        markAsManuallySelected(state, { payload }: PayloadAction<string>) {
            const conversationId = payload;
            if (!conversationId) return;

            if (!state.byConversation[conversationId]) {
                state.byConversation[conversationId] = getInitialContentData();
            }
            state.byConversation[conversationId].isManuallySelected = true;
        },

        /**
         * Mark content as auto-selected for a conversation
         * Allows auto-updates when new messages arrive
         *
         * @param state - Current state
         * @param payload - Conversation ID
         */
        markAsAutoSelected(state, { payload }: PayloadAction<string>) {
            const conversationId = payload;
            if (!conversationId) return;

            if (!state.byConversation[conversationId]) {
                state.byConversation[conversationId] = getInitialContentData();
            }
            state.byConversation[conversationId].isManuallySelected = false;
        },

        /**
         * Clear selected content for a specific conversation
         *
         * @param state - Current state
         * @param payload - Conversation ID to clear (if not provided, clears all)
         */
        clearSelectedContent(state, { payload }: PayloadAction<string | undefined>) {
            if (payload) {
                // Clear specific conversation
                delete state.byConversation[payload];
            } else {
                // Clear all conversations
                state.byConversation = {};
            }
        },
    },
});

// Actions
export const {
    setSelectedContent,
    setSqlQuery,
    setThoughtProcess,
    markAsManuallySelected,
    markAsAutoSelected,
    clearSelectedContent,
} = selectedContentSlice.actions;

// Base Selectors
/**
 * Select all content data by conversation
 */
const selectContentByConversation = (state: RootState) => state.chatSlice.selectedContent.byConversation;

// Factory Selectors (create per-component instance for optimal memoization)
/**
 * Create a memoized selector for SQL query of a specific conversation
 *
 * @returns A selector function that takes (state, conversationId) and returns the SQL query
 *
 * @example
 * const selectSqlQuery = useMemo(() => makeSelectSqlQueryForConversation(), []);
 * const sqlQuery = useAppSelector(state => selectSqlQuery(state, conversationId));
 */
export const makeSelectSqlQueryForConversation = () =>
    createSelector(
        [selectContentByConversation, (_state: RootState, conversationId: string | null) => conversationId],
        (byConversation, conversationId) => {
            if (!conversationId) return null;
            return byConversation[conversationId]?.sqlQuery ?? null;
        }
    );

/**
 * Create a memoized selector for thought process of a specific conversation
 *
 * @returns A selector function that takes (state, conversationId) and returns the thought process
 *
 * @example
 * const selectThoughtProcess = useMemo(() => makeSelectThoughtProcessForConversation(), []);
 * const thoughtProcess = useAppSelector(state => selectThoughtProcess(state, conversationId));
 */
export const makeSelectThoughtProcessForConversation = () =>
    createSelector(
        [selectContentByConversation, (_state: RootState, conversationId: string | null) => conversationId],
        (byConversation, conversationId) => {
            if (!conversationId) return null;
            return byConversation[conversationId]?.thoughtProcess ?? null;
        }
    );

/**
 * Create a memoized selector for manual selection status of a specific conversation
 *
 * @returns A selector function that takes (state, conversationId) and returns whether manually selected
 *
 * @example
 * const selectIsManuallySelected = useMemo(() => makeSelectIsManuallySelectedForConversation(), []);
 * const isManuallySelected = useAppSelector(state => selectIsManuallySelected(state, conversationId));
 */
export const makeSelectIsManuallySelectedForConversation = () =>
    createSelector(
        [selectContentByConversation, (_state: RootState, conversationId: string | null) => conversationId],
        (byConversation, conversationId) => {
            if (!conversationId) return false;
            return byConversation[conversationId]?.isManuallySelected ?? false;
        }
    );

/**
 * Create a memoized selector for selected timestamp of a specific conversation
 *
 * @returns A selector function that takes (state, conversationId) and returns the timestamp
 *
 * @example
 * const selectSelectedTimestamp = useMemo(() => makeSelectSelectedTimestampForConversation(), []);
 * const timestamp = useAppSelector(state => selectSelectedTimestamp(state, conversationId));
 */
export const makeSelectSelectedTimestampForConversation = () =>
    createSelector(
        [selectContentByConversation, (_state: RootState, conversationId: string | null) => conversationId],
        (byConversation, conversationId) => {
            if (!conversationId) return null;
            return byConversation[conversationId]?.selectedTimestamp ?? null;
        }
    );

/**
 * Create a memoized selector for selected message ID of a specific conversation
 *
 * @returns A selector function that takes (state, conversationId) and returns the message ID
 *
 * @example
 * const selectSelectedMessageId = useMemo(() => makeSelectSelectedMessageIdForConversation(), []);
 * const messageId = useAppSelector(state => selectSelectedMessageId(state, conversationId));
 */
export const makeSelectSelectedMessageIdForConversation = () =>
    createSelector(
        [selectContentByConversation, (_state: RootState, conversationId: string | null) => conversationId],
        (byConversation, conversationId) => {
            if (!conversationId) return null;
            return byConversation[conversationId]?.selectedMessageId ?? null;
        }
    );

/**
 * Create a memoized selector for complete content data of a specific conversation
 *
 * @returns A selector function that takes (state, conversationId) and returns all content data
 *
 * @example
 * const selectSelectedContent = useMemo(() => makeSelectSelectedContentForConversation(), []);
 * const content = useAppSelector(state => selectSelectedContent(state, conversationId));
 */
export const makeSelectSelectedContentForConversation = () =>
    createSelector(
        [selectContentByConversation, (_state: RootState, conversationId: string | null) => conversationId],
        (byConversation, conversationId) => {
            if (!conversationId) return getInitialContentData();
            return byConversation[conversationId] ?? getInitialContentData();
        }
    );

// Backward Compatibility Selectors (deprecated - prefer factory selectors)
/**
 * @deprecated Use makeSelectSqlQueryForConversation() factory instead
 * Select the SQL query for first conversation (for backward compatibility)
 */
export const selectSqlQuery = (state: RootState) => {
    const conversations = Object.values(state.chatSlice.selectedContent.byConversation);
    return conversations[0]?.sqlQuery ?? null;
};

/**
 * @deprecated Use makeSelectThoughtProcessForConversation() factory instead
 * Select the thought process for first conversation (for backward compatibility)
 */
export const selectThoughtProcess = (state: RootState) => {
    const conversations = Object.values(state.chatSlice.selectedContent.byConversation);
    return conversations[0]?.thoughtProcess ?? null;
};

/**
 * @deprecated Use makeSelectIsManuallySelectedForConversation() factory instead
 * Select manual selection status for first conversation (for backward compatibility)
 */
export const selectIsManuallySelected = (state: RootState) => {
    const conversations = Object.values(state.chatSlice.selectedContent.byConversation);
    return conversations[0]?.isManuallySelected ?? false;
};

// Reducer
export default selectedContentSlice.reducer;
